import { Fragment, useState } from 'react';
import {
  Button,
  cn,
  EMessageDirection,
  EWidgetType,
  Flex,
  Icon,
  type IChatMessage,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

export type TRainSubAgentTaskStatus = 'running' | 'success' | 'error';

export interface IRainSubAgentTaskCardProps {
  subAgentName: string;
  input: string;
  status: TRainSubAgentTaskStatus;
  /** Elapsed milliseconds. While running, the parent re-upserts this card on
   * every routed event so this value bumps frequently enough to feel live. */
  elapsedMs: number;
  result?: string;
  errorMessage?: string;
  nestedMessages?: IChatMessage[];
  className?: string;
}

const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds - minutes * 60);
  return `${minutes}m ${remSeconds}s`;
};

const dotClassName = (status: TRainSubAgentTaskStatus): string => {
  if (status === 'success') return 'bg-success';
  if (status === 'error') return 'bg-destructive';
  return 'bg-info';
};

export function RainSubAgentTaskCard({
  subAgentName,
  input,
  status,
  elapsedMs,
  result,
  errorMessage,
  nestedMessages,
  className,
}: IRainSubAgentTaskCardProps) {
  const { t } = useTranslation('rain');
  const [open, setOpen] = useState(false);

  const duration = formatDuration(elapsedMs);
  const hasNested = (nestedMessages?.length ?? 0) > 0;
  const hasResult = !!result;
  const hasError = !!errorMessage;
  const hasExpandableContent = hasNested || hasResult || hasError;

  const toggle = () => {
    if (!hasExpandableContent) return;
    setOpen(prev => !prev);
  };

  return (
    <Flex
      direction="column"
      gap={2}
      className={cn('w-full min-w-0', className)}
      data-testid="rain-sub-agent-task-card"
      data-status={status}
    >
      <Button
        variant="ghost"
        disabled={!hasExpandableContent}
        onClick={toggle}
        className={cn(
          'flex h-auto w-full min-w-0 items-center justify-start gap-2 px-2 py-1 text-left',
          hasExpandableContent ? 'cursor-pointer' : 'cursor-default',
        )}
        data-testid="rain-sub-agent-task-toggle"
        aria-expanded={open}
      >
        <span
          className={cn(
            'inline-block size-2 rounded-full shrink-0',
            dotClassName(status),
          )}
          aria-hidden
          data-testid="rain-sub-agent-task-dot"
        />
        <span className="text-sm font-medium text-foreground shrink-0">
          {subAgentName}
        </span>
        <span className="text-sm text-muted-foreground truncate flex-1 min-w-0">
          {input}
        </span>
        {duration && (
          <span className="text-xs text-muted-foreground shrink-0">
            {duration}
          </span>
        )}
        {hasExpandableContent && (
          <Icon
            type={open ? 'ChevronDown' : 'ChevronRight'}
            size="sm"
            className="shrink-0 text-muted-foreground"
          />
        )}
      </Button>

      {open && hasExpandableContent && (
        <Flex
          direction="column"
          gap={2}
          className="min-w-0 pl-4 border-l border-border"
          data-testid="rain-sub-agent-task-body"
        >
          {nestedMessages?.map(message => {
            if (
              message.widget?.type === EWidgetType.CUSTOM &&
              message.widget.customContent
            ) {
              return (
                <Fragment key={message.id}>
                  {message.widget.customContent}
                </Fragment>
              );
            }
            if (
              message.direction === EMessageDirection.INCOMING &&
              message.content
            ) {
              return (
                <p
                  key={message.id}
                  className="text-sm text-foreground whitespace-pre-wrap break-words"
                >
                  {message.content}
                </p>
              );
            }
            return null;
          })}

          {hasResult && (
            <Flex direction="column" gap={1} className="min-w-0">
              <span className="text-xs font-medium text-muted-foreground">
                {t('rain.subAgent.resultHeader')}
              </span>
              <pre
                className="text-xs text-foreground bg-card border border-border rounded-md p-2 overflow-x-auto whitespace-pre min-w-0 max-w-full"
                data-testid="rain-sub-agent-task-result"
              >
                {result}
              </pre>
            </Flex>
          )}

          {hasError && (
            <Flex direction="column" gap={1} className="min-w-0">
              <span className="text-xs font-medium text-destructive">
                {t('rain.subAgent.errorHeader')}
              </span>
              <pre
                className="text-xs text-destructive bg-card border border-border rounded-md p-2 overflow-x-auto whitespace-pre min-w-0 max-w-full"
                data-testid="rain-sub-agent-task-error"
              >
                {errorMessage}
              </pre>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
}
